import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'
import {
  useGetMachineryDirectiveCompliance,
  useListStandardApplicability,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ComplianceSubPageHeader } from '../../components/ComplianceSubPageHeader'
import { ComplianceSubPageTitle } from '../../components/ComplianceSubPageTitle'
import { buildMachineryDirectiveStudioBaseHref } from '../../machineryDirectiveStudioPaths'

const COMPLY_INSTRUCTION_STEPS = [
  { step: 1, path: 'standards' },
  { step: 2, path: 'hazards' },
  { step: 3, path: 'risk-reduction-measures' },
  { step: 4, path: 'risk-assessment' },
  { step: 5, path: 'manual-generation' },
  { step: 6, path: 'manual' },
] as const

const INSTRUCTION_FIELD_I18N_KEY = {
  title: { default: 'title', partlyCompleted: 'titlePartlyCompleted' },
  description: {
    default: 'description',
    partlyCompleted: 'descriptionPartlyCompleted',
  },
  action: { default: 'action', partlyCompleted: 'actionPartlyCompleted' },
} as const

function Instruction({
  step,
  title,
  description,
  actionHref,
  actionLabel,
  isDisabled,
}: {
  step: number
  title: string
  description: string
  actionHref: string
  actionLabel: string
  isDisabled: boolean
}) {
  return (
    <div className='flex gap-3'>
      <div className='flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/15 font-semibold text-primary'>
        {step}
      </div>
      <div className='min-w-0 flex-1'>
        <p className='mb-1 text-sm font-medium text-foreground'>{title}</p>
        <p className='text-sm text-muted-foreground'>{description}</p>
        {isDisabled ? (
          <Button variant='outline' size='sm' className='mt-2' disabled>
            {actionLabel}
          </Button>
        ) : (
          <Button variant='outline' size='sm' className='mt-2' asChild>
            <Link to={actionHref}>{actionLabel}</Link>
          </Button>
        )}
      </div>
    </div>
  )
}

export function IntroPage() {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const params = useParams<{
    productId: string
    machineryDirectiveComplianceId: string
  }>()
  const productId = params.productId ?? ''
  const complianceId = params.machineryDirectiveComplianceId ?? ''
  const studioBaseHref = buildMachineryDirectiveStudioBaseHref(
    productId,
    complianceId
  )
  const buildHref = (path = '') => `${studioBaseHref}${path ? `/${path}` : ''}`

  const { data: compliance } = useGetMachineryDirectiveCompliance(complianceId)
  const { data: standards = [] } = useListStandardApplicability(complianceId)
  const isPartlyCompletedMachine = compliance?.isPartlyCompletedMachine === true
  const hasNoStandards = standards.length === 0
  const allStandardsDecided =
    !hasNoStandards &&
    standards.every((standard) => standard.isAccepted != null)
  const atLeastOneAccepted = standards.some(
    (standard) => standard.isAccepted === true
  )
  const allDecidedButNoneAccepted =
    !hasNoStandards && allStandardsDecided && !atLeastOneAccepted

  const instructionKey = (
    step: number,
    field: keyof typeof INSTRUCTION_FIELD_I18N_KEY
  ) => {
    const usePartlyCompleted =
      isPartlyCompletedMachine && (step === 5 || step === 6)
    const suffix = usePartlyCompleted
      ? INSTRUCTION_FIELD_I18N_KEY[field].partlyCompleted
      : INSTRUCTION_FIELD_I18N_KEY[field].default
    return `machineryDirective.instructions.${step}.${suffix}` as const
  }

  return (
    <div className='space-y-6'>
      <ComplianceSubPageHeader>
        <div>
          <ComplianceSubPageTitle>
            {t('machineryDirective.title')}
          </ComplianceSubPageTitle>
          <p className='text-lg text-muted-foreground'>
            {t('machineryDirective.description')}
          </p>
        </div>
      </ComplianceSubPageHeader>

      <Card className='border-border bg-card'>
        <CardHeader>
          <CardTitle>{t('machineryDirective.instructions.title')}</CardTitle>
        </CardHeader>
        <CardContent className='space-y-4'>
          {COMPLY_INSTRUCTION_STEPS.map(({ step, path }) => (
            <Instruction
              key={step}
              step={step}
              title={t(instructionKey(step, 'title'))}
              description={t(instructionKey(step, 'description'))}
              actionHref={buildHref(path)}
              actionLabel={t(instructionKey(step, 'action'))}
              isDisabled={allDecidedButNoneAccepted && path !== 'standards'}
            />
          ))}
        </CardContent>
      </Card>
    </div>
  )
}
